<template>
  <v-container id="regular-forms" fluid tag="section">
    <v-row>
      <v-btn fab dark small color="primary" @click="dialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-dialog v-model="dialog">
        <v-col cols="12">
          <base-material-card
            color="primary"
            icon="mdi-file"
            :title="$route.params.id ? $t('Edit') : $t('New')"
            class="px-5 py-3"
          >
            <v-row>
              <v-col cols="6">
                <v-form ref="form" autocomplete="new-password" lazy-validation>
                  <v-row>
                    <v-col>
                      <h3>{{ $t("Upload file") }}</h3>
                      <v-text-field
                        v-model="formValues.name"
                        label="Name"
                        v-validate="'required'"
                        required
                        data-vv-name="name"
                        :error-messages="errors.collect('name')"
                      ></v-text-field>
                      <v-select v-model="formValues.type" :items="fileTypes">
                        label="Type" v-validate="'required'" required
                        data-vv-name="type"
                        :error-messages="errors.collect('type')"
                      </v-select>
                      <base-date-picker
                        v-model="formValues.expire"
                      ></base-date-picker>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <base-file-uploader
                        v-on:new-data="onFileInput"
                        v-model="fileData"
                      ></base-file-uploader>
                    </v-col>
                  </v-row>
                  <v-card-actions class="pl-0">
                    <v-btn color="primary" class="mr-4" @click="close()">
                      {{ $t("back") }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" class="mr-4" @click="saveItem">
                      {{ $t("save") }}
                    </v-btn>
                  </v-card-actions>
                </v-form>
              </v-col>
              <v-col cols="6">
                <v-card class="grey darken-2">
                  <v-card-title>
                    <h4 class="float-left white--text">
                      {{ $t("Select file") }}
                    </h4>
                    <v-spacer></v-spacer>
                    <v-text-field
                      v-model="search"
                      append-icon="mdi-magnify"
                      :label="$t('search')"
                      single-line
                      hide-details
                      class="float-right"
                      dark
                    ></v-text-field>
                  </v-card-title>
                  <v-data-table
                    :headers="headers"
                    :items="selectFiles"
                    :items-per-page="10"
                    class="elevation-1"
                    :search="search"
                    :footer-props="{
                      'items-per-page-options': [10, 15, 50, -1]
                    }"
                    :custom-filter="customFilter"
                    :loading="loading"
                    loading-text="Loading... Please wait"
                  >
                    <template v-slot:item.thumb="{ item }">
                      <v-btn icon>
                        <a :href="item.url | addToken" target="_blank">
                          <v-img
                            v-if="item.mime_type.includes('image')"
                            :src="item.thumb | addToken"
                            height="50"
                            width="100"
                            contain
                            flat
                          ></v-img>
                          <v-icon v-else>mdi-file</v-icon>
                        </a>
                      </v-btn>
                    </template>
                    <template v-slot:item.name="{ item }">
                      <a @click="selectFile(item)">{{ item.name }}</a>
                    </template>
                    <template v-slot:item.type="{ item }">
                      <v-chip small v-if="item.type" dark>{{
                        item.type
                      }}</v-chip>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      {{ moment(item.created_at).format("DD.MM.YYYY") }}
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </base-material-card>
        </v-col>
      </v-dialog>
    </v-row>
    <v-row v-if="filteredFiles.length">
      <v-card width="100%">
        <v-list two-line subheader>
          <v-list-item-group color="primary">
            <draggable :list="filteredFiles" @sort="onSort">
              <v-list-item
                v-for="(file, index) in filteredFiles"
                :key="index"
                :title="file.name"
              >
                <v-list-item-avatar @click="open(file)">
                  <v-img
                    v-if="file.mime_type.includes('image')"
                    :src="file.thumb | addToken"
                  ></v-img>
                  <v-icon v-else>mdi-file</v-icon>
                </v-list-item-avatar>
                <v-list-item-content @click="open(file)" class="ml-2 mr-2">
                  <v-list-item-title v-text="file.name"></v-list-item-title>
                  <v-list-item-subtitle
                    v-text="file.type"
                  ></v-list-item-subtitle>
                </v-list-item-content>
                <v-spacer></v-spacer>
                <v-list-item-action>
                  <v-btn small fab @click="removeDocument(file)">
                    <v-icon small color="red">
                      mdi-minus-circle-outline
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </draggable>
          </v-list-item-group>
        </v-list>
      </v-card>
    </v-row>
  </v-container>
</template>

<script>
import { saveItem, fetchItems } from "../../../documents/shared/services";
import draggable from "vuedraggable";

export default {
  name: "app-document-item",
  components: {
    draggable
  },
  $_veeValidate: {
    validator: "new"
  },
  props: {
    uploadCallback: null,
    removeCallback: null,
    sortCallback: null,
    files: null,
    type: null
  },
  data() {
    return {
      formValues: {
        name: ""
      },
      fileData: {},
      permissions: [],
      loading: true,
      dialog: false,
      selectFiles: [],
      fileTypes: [
        { text: "Document", value: "document" },
        { text: "Image", value: "image" },
        { text: "Certificate", value: "certificate" },
        { text: "Icon", value: "icon" }
      ],
      headers: [
        {
          text: "",
          align: "start",
          value: "thumb"
        },
        {
          text: "Name",
          align: "start",
          value: "name"
        },
        {
          text: "Date",
          value: "created_at"
        },
        {
          text: "Typ",
          value: "type",
          sortable: false
        }
      ],
      search: "",
      filteredFiles: []
    };
  },
  mounted() {
    this.filterFiles();
  },
  computed: {
    computedFormValues() {
      let fileData = {
        path: this.fileData.path ? this.fileData.path : this.formValues.path,
        mime_type: this.fileData.mime_type
          ? this.fileData.mime_type
          : this.formValues.mime_type
      };

      this.$delete(this.formValues, "image_data");

      return {
        ...this.formValues,
        ...fileData
      };
    }
  },
  methods: {
    async saveItem() {
      this.$validator.reset();
      this.$validator.validateAll();
      let resp = await saveItem(null, this.computedFormValues, this.$validator);
      if (resp.success) {
        this.selectFile(resp.data);
      }
    },
    selectFile(file) {
      this.uploadCallback({ ...file, type: this.type });
      (this.formValues = {
        name: ""
      }),
        (this.dialog = false);
    },
    onFileInput() {
      if (!this.formValues.name) {
        this.formValues.name = this.fileData.name;
      }
    },
    onSort() {
      let files = this.filteredFiles.map(_file => {
        return {
          ..._file,
          type: this.type
        };
      });
      if (this.sortCallback) {
        this.sortCallback(files);
      }
    },
    close() {
      this.dialog = false;
    },
    async removeDocument(document) {
      if (
        await this.$root.$confirm(this.$t("Delete"), this.$t("Are you sure?"), {
          color: "red"
        })
      ) {
        this.removeCallback(document);
      }
    },
    open(file) {
      window.open(this.$options.filters.addToken(file.url), "_blank");
    },
    filterFiles() {
      if (this.type) {
        this.filteredFiles = this.files.filter(file => {
          return file.type
            .replace(" ", "")
            .split(",")
            .includes(this.type);
          // return file.type == this.type;
        });
      } else {
        this.filteredFiles = this.files;
      }
    },
    customFilter(value, search, item) {
      search = search
        .toString()
        .toLowerCase()
        .split(/\s+/);
      let search1 = search[0];
      let search2 = search[1];
      let search3 = search[2];
      if (search3) {
        return (
          item.name.toLowerCase().indexOf(search1) > -1 &&
          item.name.toLowerCase().indexOf(search2) > -1 &&
          item.name.toLowerCase().indexOf(search3) > -1
        );
      } else if (search2) {
        return (
          item.name.toLowerCase().indexOf(search1) > -1 &&
          item.name.toLowerCase().indexOf(search2) > -1
        );
      } else {
        return item.name.toLowerCase().indexOf(search1) > -1;
      }
    }
  },
  watch: {
    files: {
      handler(val) {
        this.filterFiles();
      }
    },
    type: {
      handler(val) {
        this.filterFiles();
      }
    },
    dialog: {
      handler(val) {
        if (!this.selectFiles.length) {
          fetchItems().then(res => {
            this.selectFiles = res.data;
            this.loading = false;
          });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
